import { ITableAction, ITableColumn, ITableData } from '../../interfaces/tableProps';
import { useCallback, useMemo } from 'react';
import AppTable from '../common/AppTable';
import { theme } from '../../theme';
import { ColumnFlexBox } from '../common/boxes';
import SearchHeader from '../common/SearchHeader';
import { Box } from '@mui/material';
import { ISearchHeaderProps } from '../../interfaces/searchHeaderProps';
import { IProjectPositionSearchResponse } from '../../interfaces/projectPostitionSearch';
import { CLIENTS_PATH, PROJECT_OPPORTUNITIES_PATH } from '../../router/paths';
import { IPaymentRate } from '../../interfaces/paymentRate';
import { Delete, Edit, Groups, Info, PersonAdd } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { TEXTS } from '../../core/texts';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../interfaces/errorResponse';
import { parseAxiosError } from '../../services/axios';
import { IProjectPosition } from '../../interfaces/projectPosition';
import { useNotification } from '../../+state/useNotification';
import { PROJECT_POSITION_STATUS_MAP, ProjectPositionStatus } from '../../enums/projectPositionStatus';
import { POSITION_DETAILS_QUERY_PARAM, POSITION_ENTITY_QUERY_PARAM } from './JobBoard';
import { useSearchParams } from 'react-router-dom';
import { ADD_APPLICANT_QUERY_PARAM, APPLICANTS_QUERY_PARAM } from '../project-opportunities/project-opportunity-entity/ProjectOpportunityEntityPositions';
import { useConfirmationDialog } from '../../+state/useConfirmationDialog';
import {
  MODAL_PAGINATION_PAGE_NAMESPACE,
  MODAL_PAGINATION_SIZE_NAMESPACE,
  PAGINATION_DEFAULT_PAGE, PAGINATION_DEFAULT_SIZE
} from '../../core/pagination';
import { PAYMENT_RATE_MAP } from '../../enums/paymentRateType';
import { AVAILABILITY_TYPE_MAP, AvailabilityType } from '../../enums/AvailabilityType';

const initialColumns: ITableColumn<keyof IProjectPositionSearchResponse>[] = [
  { name: 'positionTitle', label: 'Title' },
  { name: 'clientName', label: 'Client', sx: { '&:hover': { cursor: 'pointer', textDecoration: 'underline', color: theme.palette.primary.dark } } },
  { name: 'roleName', label: 'Role'},
  { name: 'positionName', label: 'Specialty'},
  { name: 'skills', label: 'Skills', format: (skills: string[]) => skills.join(', '), truncate: 80 },
  { name: 'status', label: 'Status', format: (val: ProjectPositionStatus) => PROJECT_POSITION_STATUS_MAP[val] },
  { name: 'availabilityType', label: 'Availability', format: (val: AvailabilityType) => AVAILABILITY_TYPE_MAP[val] },
  {
    name: 'rate',
    label: 'Rate',
    format: (rate: IPaymentRate) => `${rate.amount} ${rate.currency}/${PAYMENT_RATE_MAP[rate.type]}`,
  },
  {
    name: 'ageInDays',
    label: 'Published',
    format: (ageInDays: number) => ageInDays === 0 ? "Today" : (ageInDays + " days ago")
  }
];

interface IProps {
  tableData: ITableData<IProjectPositionSearchResponse>;
  searchProps: ISearchHeaderProps;
  handleDeletePosition: (pos: IProjectPositionSearchResponse) => Promise<IProjectPosition>;
  refreshPositions: () => void;
}

const JobBoardTableWithSearch = ({
                                   tableData,
                                   searchProps,
                                   handleDeletePosition,
                                   refreshPositions
                                 }: IProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const handlers: { [key: string]: (val: IProjectPositionSearchResponse) => void } = useMemo(() => ({
    projectOpportunityId: (f) => window.open(PROJECT_OPPORTUNITIES_PATH + `/${f.clientId}/${f.projectOpportunityId}`, '_blank', 'noopener,noreferrer'),
    clientName: (f) => window.open(CLIENTS_PATH + `/${f.clientId}`, '_blank', 'noopener,noreferrer'),
  }), []);

  const { openErrorToast, openSuccessToast } = useNotification();
  const { openDialog } = useConfirmationDialog();

  const handleDetailsClick = useCallback((pos: IProjectPositionSearchResponse) => {
    searchParams.set(POSITION_DETAILS_QUERY_PARAM, pos.id);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const handleEditClick = useCallback((searchRes: IProjectPositionSearchResponse) => {
    searchParams.set(POSITION_ENTITY_QUERY_PARAM, searchRes.id);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const handleApplicantsIcon = useCallback((position: IProjectPosition) => {
    searchParams.set(APPLICANTS_QUERY_PARAM, position.id);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const handleAddApplicantIcon = useCallback((position: IProjectPosition) => {
    searchParams.set(MODAL_PAGINATION_PAGE_NAMESPACE, PAGINATION_DEFAULT_PAGE.toString());
    searchParams.set(MODAL_PAGINATION_SIZE_NAMESPACE, PAGINATION_DEFAULT_SIZE.toString());
    searchParams.set(ADD_APPLICANT_QUERY_PARAM, position.id);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const deleteMutation = useMutation({
    mutationFn: (search: IProjectPositionSearchResponse) => handleDeletePosition(search),
    onSuccess: () => {
      openSuccessToast(TEXTS.SUCCESS_DELETED);
      refreshPositions();
    },
    onError: (err: AxiosError<IErrorResponse>) => openErrorToast(parseAxiosError(err))
  });

  const handleDeleteIcon = useCallback((search: IProjectPositionSearchResponse) => {
    openDialog({ cb: () => deleteMutation.mutate(search) });
  }, [openDialog, deleteMutation]);

  const actions = useMemo<ITableAction[]>(() => ([
    {
      icon: <Info color={'secondary'}/>,
      tooltip: 'Info',
      onClick: handleDetailsClick,
    },
    {
      icon: <PersonAdd color={'secondary'}/>,
      tooltip: 'Add Applicant',
      onClick: handleAddApplicantIcon,
    },
    {
      icon: <Groups color={'secondary'}/>,
      tooltip: 'Applicants',
      onClick: handleApplicantsIcon
    },
    {
      icon: <Edit color={'primary'}/>,
      tooltip: 'Edit Position',
      onClick: handleEditClick
    },
    {
      icon: <Delete color={'error'}/>,
      tooltip: 'Delete',
      onClick: handleDeleteIcon,
      condition: (search: IProjectPositionSearchResponse) => search.status !== ProjectPositionStatus.REMOVED
    },
  ]), [handleDetailsClick, handleEditClick, handleDeleteIcon, handleAddApplicantIcon, handleApplicantsIcon]);

  return (
    <ColumnFlexBox>
      <Box mb={6}>
        <SearchHeader {...searchProps} initialExpanded={true}/>
      </Box>
      <AppTable
        data={tableData}
        columns={initialColumns}
        handlers={handlers}
        labelRowsPerPage={'Positions per page'}
        actions={actions}
      />
    </ColumnFlexBox>
  );
};

export default JobBoardTableWithSearch;